import React, { useState } from 'react';
import { X, Copy, Check, Code, Link, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const FRONTEND_URL = window.location.origin;

export default function EmbedCodeModal({ submission, onClose }) {
    const [copied, setCopied] = useState(null);

    if (!submission) return null;

    const viewUrl = `${FRONTEND_URL}/testimonial/view/${submission.submission_id}`;
    const iframeCode = `<iframe src="${viewUrl}?embed=true" width="100%" height="400" frameborder="0" style="border:none;border-radius:12px;overflow:hidden;" allowfullscreen></iframe>`;
    const scriptCode = `<div id="testimonial-${submission.submission_id}"></div>\n<script>\n(function(){\n  var d=document,f=d.createElement('iframe');\n  f.src='${viewUrl}?embed=true';\n  f.style='width:100%;border:none;border-radius:12px;overflow:hidden';\n  f.height='400';\n  d.getElementById('testimonial-${submission.submission_id}').appendChild(f);\n  window.addEventListener('message',function(e){\n    if(e.data&&e.data.type==='testimonial-resize')f.height=e.data.height;\n  });\n})();\n</script>`;

    const copy = async (text, key) => {
        await navigator.clipboard.writeText(text);
        setCopied(key);
        toast.success('Copied!');
        setTimeout(() => setCopied(null), 2000);
    };

    const shareUrl = (platform) => {
        const text = `Check out this testimonial!`;
        const urls = {
            twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(viewUrl)}`,
            linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(viewUrl)}`,
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(viewUrl)}`,
        };
        window.open(urls[platform], '_blank', 'width=600,height=400');
    };

    // `!mt-0`: Testimonials mounts this as a later child of its root's
    // `space-y-6`, whose margin would push the fixed overlay 24px down (#496).
    return (
        <div className="fixed inset-0 z-50 !mt-0 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
                    <h2 className="text-base font-semibold text-stone-900">Embed & Share</h2>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-stone-100"><X className="w-4 h-4" /></button>
                </div>

                <div className="p-5 space-y-5">
                    {/* Direct link */}
                    <div>
                        <label className="flex items-center gap-1.5 text-xs font-medium text-stone-600 mb-1.5">
                            <Link className="w-3.5 h-3.5" /> Direct Link
                        </label>
                        <div className="flex gap-2">
                            <input value={viewUrl} readOnly className="flex-1 px-3 py-2 text-sm bg-stone-50 border border-stone-200 rounded-lg" />
                            <button onClick={() => copy(viewUrl, 'url')}
                                className="px-3 py-2 bg-stone-900 text-white rounded-lg text-sm hover:bg-stone-800">
                                {copied === 'url' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>

                    {/* Iframe embed */}
                    <div>
                        <label className="flex items-center gap-1.5 text-xs font-medium text-stone-600 mb-1.5">
                            <Code className="w-3.5 h-3.5" /> Iframe Embed
                        </label>
                        <div className="relative">
                            <pre className="p-3 bg-stone-50 border border-stone-200 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap">{iframeCode}</pre>
                            <button onClick={() => copy(iframeCode, 'iframe')}
                                className="absolute top-2 right-2 p-1.5 bg-white border border-stone-200 rounded hover:bg-stone-50">
                                {copied === 'iframe' ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                            </button>
                        </div>
                    </div>

                    {/* Script embed */}
                    <div>
                        <label className="flex items-center gap-1.5 text-xs font-medium text-stone-600 mb-1.5">
                            <Code className="w-3.5 h-3.5" /> Auto-resize Script
                        </label>
                        <div className="relative">
                            <pre className="p-3 bg-stone-50 border border-stone-200 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap max-h-40">{scriptCode}</pre>
                            <button onClick={() => copy(scriptCode, 'script')}
                                className="absolute top-2 right-2 p-1.5 bg-white border border-stone-200 rounded hover:bg-stone-50">
                                {copied === 'script' ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                            </button>
                        </div>
                    </div>

                    {/* Social share */}
                    <div>
                        <label className="flex items-center gap-1.5 text-xs font-medium text-stone-600 mb-1.5">
                            <Share2 className="w-3.5 h-3.5" /> Share
                        </label>
                        <div className="flex gap-2">
                            {[
                                { key: 'twitter', label: 'Twitter', bg: 'bg-sky-500 hover:bg-sky-600' },
                                { key: 'linkedin', label: 'LinkedIn', bg: 'bg-blue-600 hover:bg-blue-700' },
                                { key: 'facebook', label: 'Facebook', bg: 'bg-blue-500 hover:bg-blue-600' },
                            ].map(s => (
                                <button key={s.key} onClick={() => shareUrl(s.key)}
                                    className={`px-3 py-1.5 rounded-lg text-white text-xs font-medium ${s.bg}`}>
                                    {s.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
